import { useEffect, useState } from "react";
import { api, type PublicSiteSettings } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";
import PartnersSettings from "./PartnersSettings";

type AssetKey =
  | "logo-full"
  | "logo-icon"
  | "logo-dark"
  | "favicon"
  | "hero-home"
  | "hero-login"
  | "hero-validate"
  | "course-default"
  | "og-image"
  | "certificate-logo"
  | "certificate-stamp"
  | "landing-about"
  | "landing-impact"
  | "landing-careers"
  | "landing-contact";

const CERTIFICATE_ASSET_KEYS: AssetKey[] = ["certificate-logo", "certificate-stamp"];

const ASSET_LABELS: Record<AssetKey, { label: string; hint: string }> = {
  "logo-full": { label: "Logo completo", hint: "Navbar y footer. Si es blanco/claro, NO lo uses en certificados." },
  "logo-icon": { label: "Logo icono", hint: "Sidebar colapsado" },
  "logo-dark": { label: "Logo oscuro (fondos claros)", hint: "Versión oscura para fondos blancos" },
  favicon: { label: "Favicon", hint: "Icono de pestaña del navegador" },
  "hero-home": { label: "Hero landing", hint: "Fondo principal de la página de inicio" },
  "hero-login": { label: "Hero login", hint: "Fondo de pantalla de acceso" },
  "hero-validate": { label: "Hero validar certificado", hint: "Fondo de validación pública" },
  "course-default": { label: "Imagen curso por defecto", hint: "Cuando un curso no tiene foto" },
  "og-image": { label: "Imagen Open Graph", hint: "Vista previa al compartir en redes (1200×630 recomendado)" },
  "certificate-logo": {
    label: "Logo del certificado",
    hint: "Logo superior del PDF (debe ser OSCURO sobre fondo blanco). Si vacío, se usa logo oscuro o el logo oficial KSM.",
  },
  "certificate-stamp": {
    label: "Sello del certificado",
    hint: "Imagen esquina superior derecha. Si no hay sello, no se muestra nada (sin círculo 'oficial').",
  },
  "landing-about": { label: "Landing: Nosotros", hint: "Sección quiénes somos" },
  "landing-impact": { label: "Landing: Impacto", hint: "Sección de resultados" },
  "landing-careers": { label: "Landing: Carreras", hint: "Sección únete al equipo" },
  "landing-contact": { label: "Landing: Contacto", hint: "Fondo del formulario de contacto" },
};

const PREVIEW_KEYS: Record<AssetKey, keyof PublicSiteSettings> = {
  "logo-full": "logo_full",
  "logo-icon": "logo_icon",
  "logo-dark": "logo_dark",
  favicon: "favicon",
  "hero-home": "hero_home",
  "hero-login": "hero_login",
  "hero-validate": "hero_validate",
  "course-default": "course_default",
  "og-image": "og_image",
  "certificate-logo": "certificate_logo",
  "certificate-stamp": "certificate_stamp",
  "landing-about": "landing_about",
  "landing-impact": "landing_impact",
  "landing-careers": "landing_careers",
  "landing-contact": "landing_contact",
};

const STAMP_SIZE_OPTIONS = [
  { value: "64", label: "Pequeño (64px)" },
  { value: "80", label: "Mediano (80px)" },
  { value: "96", label: "Normal (96px)" },
  { value: "120", label: "Grande (120px)" },
  { value: "144", label: "Extra (144px)" },
];

export default function ConfiguracionPage() {
  const branding = useBranding();
  const [form, setForm] = useState({
    site_name: "",
    site_tagline: "",
    hero_title: "",
    hero_title_accent: "",
    hero_description: "",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    contact_email: "",
    contact_phone: "",
    contact_address: "",
    contact_secondary_email: "",
    social_facebook: "",
    social_instagram: "",
    social_linkedin: "",
    site_url: "",
  });
  const [stampSize, setStampSize] = useState("96");
  const [saving, setSaving] = useState(false);
  const [savingStamp, setSavingStamp] = useState(false);
  const [uploading, setUploading] = useState<AssetKey | null>(null);
  const [clearing, setClearing] = useState<AssetKey | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setForm({
      site_name: branding.site_name,
      site_tagline: branding.site_tagline,
      hero_title: branding.hero_title || "",
      hero_title_accent: branding.hero_title_accent || "",
      hero_description: branding.hero_description || "",
      meta_title: branding.meta_title,
      meta_description: branding.meta_description,
      meta_keywords: branding.meta_keywords,
      contact_email: branding.contact_email,
      contact_phone: branding.contact_phone,
      contact_address: branding.contact_address,
      contact_secondary_email: branding.contact_secondary_email,
      social_facebook: branding.social_facebook,
      social_instagram: branding.social_instagram,
      social_linkedin: branding.social_linkedin,
      site_url: branding.site_url,
    });
    setStampSize(branding.certificate_stamp_size || "96");
  }, [branding]);

  const handleSaveText = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await api.updateSettings(form);
      await branding.refresh();
      setMessage("Configuración guardada.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al guardar");
    }
    setSaving(false);
  };

  const handleSaveStampSize = async () => {
    setSavingStamp(true);
    setError("");
    setMessage("");
    try {
      await api.updateSettings({ certificate_stamp_size: stampSize } as Partial<PublicSiteSettings>);
      await branding.refresh();
      setMessage("Tamaño del sello guardado.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al guardar tamaño del sello");
    }
    setSavingStamp(false);
  };

  const handleUpload = async (asset: AssetKey, file: File | null) => {
    if (!file) return;
    setUploading(asset);
    setError("");
    setMessage("");
    try {
      await api.uploadBrandingAsset(asset, file);
      await branding.refresh();
      setMessage(`${ASSET_LABELS[asset].label} actualizado.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al subir archivo");
    }
    setUploading(null);
  };

  const handleClear = async (asset: AssetKey) => {
    if (!confirm(`¿Eliminar «${ASSET_LABELS[asset].label}» y dejarla vacía?`)) return;
    setClearing(asset);
    setError("");
    setMessage("");
    try {
      await api.clearBrandingAsset(asset);
      await branding.refresh();
      setMessage(`${ASSET_LABELS[asset].label} eliminada.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al eliminar imagen");
    }
    setClearing(null);
  };

  const renderAssetActions = (key: AssetKey, src: string) => (
    <div className="flex flex-wrap items-center gap-2">
      <label className="btn-secondary text-xs inline-flex items-center gap-2 cursor-pointer">
        <i className="ri-upload-2-line"></i>
        {uploading === key ? "Subiendo..." : "Subir imagen"}
        <input
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.svg,.ico"
          className="hidden"
          disabled={uploading === key || clearing === key}
          onChange={(e) => handleUpload(key, e.target.files?.[0] || null)}
        />
      </label>
      {src ? (
        <button
          type="button"
          className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 cursor-pointer disabled:opacity-50"
          disabled={uploading === key || clearing === key}
          onClick={() => handleClear(key)}
        >
          <i className="ri-delete-bin-line"></i>
          {clearing === key ? "Eliminando..." : "Eliminar"}
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]">
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Configuración del sitio</h1>
        <p className="text-sm text-[var(--color-text-muted)] mt-1">
          Logos, imágenes y SEO — los cambios se aplican sin modificar código
        </p>
      </div>

      {message && <div className="card border-l-4 border-l-green-500 text-sm text-green-400">{message}</div>}
      {error && <div className="card border-l-4 border-l-red-500 text-sm text-red-400">{error}</div>}

      <form onSubmit={handleSaveText} className="card space-y-4">
        <h2 className="font-semibold flex items-center gap-2">
          <i className="ri-search-eye-line text-[var(--color-accent-oil)]"></i>
          SEO y datos generales
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input className="input-field" placeholder="Nombre del sitio" value={form.site_name} onChange={(e) => setForm({ ...form, site_name: e.target.value })} />
          <input className="input-field" placeholder="Eslogan" value={form.site_tagline} onChange={(e) => setForm({ ...form, site_tagline: e.target.value })} />
        </div>
        <div>
          <label className="text-sm font-medium block mb-1">Título del hero (landing)</label>
          <p className="text-xs text-[var(--color-text-muted)] mb-2">
            Primera línea en blanco y segunda en dorado (acento).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              className="input-field"
              placeholder="Formamos al talento"
              value={form.hero_title}
              onChange={(e) => setForm({ ...form, hero_title: e.target.value })}
            />
            <input
              className="input-field"
              placeholder="que mueve la industria"
              value={form.hero_title_accent}
              onChange={(e) => setForm({ ...form, hero_title_accent: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium block mb-1">Texto del hero (landing)</label>
          <p className="text-xs text-[var(--color-text-muted)] mb-2">
            Párrafo bajo el título principal (ej. mención Oil &amp; Gas, años de experiencia, etc.).
          </p>
          <textarea
            className="input-field min-h-[100px]"
            placeholder="Texto descriptivo del hero"
            value={form.hero_description}
            onChange={(e) => setForm({ ...form, hero_description: e.target.value })}
          />
        </div>
        <input className="input-field" placeholder="URL pública (https://tudominio.com)" value={form.site_url} onChange={(e) => setForm({ ...form, site_url: e.target.value })} />
        <p className="text-xs text-[var(--color-text-muted)]">Debe coincidir con FRONTEND_URL del servidor — usada en QR de certificados y meta canonical.</p>
        <input className="input-field" placeholder="Título meta (SEO)" value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} />
        <textarea className="input-field min-h-[80px]" placeholder="Descripción meta" value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} />
        <input className="input-field" placeholder="Palabras clave (separadas por coma)" value={form.meta_keywords} onChange={(e) => setForm({ ...form, meta_keywords: e.target.value })} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input type="email" className="input-field" placeholder="Email principal" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
          <input className="input-field" placeholder="Teléfono" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
          <input className="input-field" placeholder="Dirección (pie de página)" value={form.contact_address} onChange={(e) => setForm({ ...form, contact_address: e.target.value })} />
          <input type="email" className="input-field" placeholder="Email secundario" value={form.contact_secondary_email} onChange={(e) => setForm({ ...form, contact_secondary_email: e.target.value })} />
        </div>
        <h3 className="font-semibold text-sm mt-4 text-[var(--color-text-secondary)]">Redes Sociales</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input className="input-field" placeholder="Enlace Facebook" value={form.social_facebook} onChange={(e) => setForm({ ...form, social_facebook: e.target.value })} />
          <input className="input-field" placeholder="Enlace Instagram" value={form.social_instagram} onChange={(e) => setForm({ ...form, social_instagram: e.target.value })} />
          <input className="input-field" placeholder="Enlace LinkedIn" value={form.social_linkedin} onChange={(e) => setForm({ ...form, social_linkedin: e.target.value })} />
        </div>
        <button type="submit" disabled={saving} className="btn-primary cursor-pointer disabled:opacity-50">
          {saving ? "Guardando..." : "Guardar SEO y textos"}
        </button>
      </form>

      <div className="card space-y-6 border border-[var(--color-accent-oil)]/30">
        <h2 className="font-semibold flex items-center gap-2">
          <i className="ri-award-line text-[var(--color-accent-oil)]"></i>
          Certificados (logo y sello)
        </h2>
        <p className="text-xs text-[var(--color-text-muted)] -mt-3">
          El logo del certificado debe ser oscuro/visible sobre fondo blanco. Sin sello no aparece ningún círculo oficial.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {CERTIFICATE_ASSET_KEYS.map((key) => {
            const previewKey = PREVIEW_KEYS[key];
            const src = String(branding[previewKey] || "");
            return (
              <div key={key} className="p-4 rounded-xl bg-[var(--color-bg-tertiary)] border border-[var(--color-border)]">
                <p className="font-medium text-sm">{ASSET_LABELS[key].label}</p>
                <p className="text-xs text-[var(--color-text-muted)] mb-3">{ASSET_LABELS[key].hint}</p>
                <div className="mb-3 rounded-lg overflow-hidden bg-white border border-[var(--color-border)] h-20 flex items-center justify-center p-3">
                  {src ? (
                    <img src={src} alt="" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <span className="text-[11px] text-gray-400">
                      {key === "certificate-logo" ? "Se usará el logo oficial KSM" : "Sin sello"}
                    </span>
                  )}
                </div>
                {renderAssetActions(key, src)}
              </div>
            );
          })}
        </div>
        <div className="p-4 rounded-xl bg-[var(--color-bg-tertiary)] border border-[var(--color-border)] flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <label className="font-medium text-sm block mb-1">Tamaño del sello</label>
            <p className="text-xs text-[var(--color-text-muted)] mb-2">Solo aplica si hay imagen de sello cargada.</p>
            <select
              className="input-field"
              value={stampSize}
              onChange={(e) => setStampSize(e.target.value)}
            >
              {STAMP_SIZE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            disabled={savingStamp}
            onClick={handleSaveStampSize}
            className="btn-secondary text-sm cursor-pointer disabled:opacity-50 whitespace-nowrap"
          >
            {savingStamp ? "Guardando..." : "Guardar tamaño"}
          </button>
        </div>
      </div>

      <div className="card space-y-6">
        <h2 className="font-semibold flex items-center gap-2">
          <i className="ri-image-line text-[var(--color-accent-oil)]"></i>
          Logos e imágenes del sitio
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(Object.keys(ASSET_LABELS) as AssetKey[])
            .filter((key) => !CERTIFICATE_ASSET_KEYS.includes(key))
            .map((key) => {
            const previewKey = PREVIEW_KEYS[key];
            const src = String(branding[previewKey] || "");
            const isHero = key.startsWith("hero-") || key === "og-image" || key === "course-default";
            return (
              <div key={key} className="p-4 rounded-xl bg-[var(--color-bg-tertiary)] border border-[var(--color-border)]">
                <p className="font-medium text-sm">{ASSET_LABELS[key].label}</p>
                <p className="text-xs text-[var(--color-text-muted)] mb-3">{ASSET_LABELS[key].hint}</p>
                <div className={`mb-3 rounded-lg overflow-hidden bg-[var(--color-bg-primary)] border border-[var(--color-border)] ${isHero ? "h-28" : "h-16 flex items-center justify-center p-2"}`}>
                  {src ? (
                    <img src={src} alt="" className={isHero ? "w-full h-full object-cover" : "max-h-full max-w-full object-contain"} />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-[11px] text-[var(--color-text-muted)]">Sin imagen</div>
                  )}
                </div>
                {renderAssetActions(key, src)}
              </div>
            );
          })}
        </div>
      </div>

      <PartnersSettings />
    </div>
  );
}
